import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppNavigate, useAppSelector, usePermissions } from "../../app/hooks";
import { selectCurrentToken } from "../../app/features/auth/authSlice";
import { selectCurrentProject } from "../../app/features/project/projectSlice";
import { encodeProjectId } from "@utils/projectIdEncoder";

import HeaderActions from "./HeaderActions";
import HihHeaderRibbon from "./HihHeaderRibbon";
import faoLogo from "../../assets/images/FAO_Logo.svg";
import hihInvestmentForumLogo from "@assets/images/hih-initiative-en.svg";

const Header = () => {
	const { pathname } = useLocation();
	const [isHeaderSmall, setIsHeaderSmall] = useState(true);

	const shouldUseSmallHeader = useMemo(() =>
		!!(pathname === "/" || pathname.startsWith("/auth") || pathname.startsWith("/hih-investment-forum") || pathname.startsWith("/help")),
		[pathname]
	);

	const isHihForumRoute = useMemo(
		() => pathname.startsWith("/hih-investment-forum"),
		[pathname]
	);

	const shouldShowHihInvestmentForumLogo = useMemo(() => {
		return pathname.startsWith("/auth") || pathname === "/";
	}, [pathname]);

	useEffect(() => {
		setIsHeaderSmall(shouldUseSmallHeader);
	}, [shouldUseSmallHeader]);

	return (
		<div
			className={`w-100 ${isHeaderSmall ? "d-flex justify-content-center" : ""} ${isHihForumRoute ? "hih-app-header" : ""}`}
		>
			{isHihForumRoute && <HihHeaderRibbon />}
			<header className={`main-header ${isHeaderSmall ? "header-login" : ""}`}>
				<div style={{ display: "flex", alignItems: "center" }}>
					<LogoSection />
					<ExactLogo />
					{shouldShowHihInvestmentForumLogo && <HihInvestmentForumLogo />}
				</div>
				<HeaderActions isHeaderSmall={isHeaderSmall} />
			</header>
		</div>
	);
};

const HihInvestmentForumLogo = () => {
	const navigate = useNavigate();
	const handleClick = () => {
		navigate("/hih-investment-forum");
	};

	return (
		<img src={hihInvestmentForumLogo} className="clickable" alt="Sustainable Investments" style={{ height: "70px" }} onClick={handleClick} />
	);
};


const LogoSection = () => (
	<div
		className="d-flex align-items-start"
		style={{
			borderRight: '1px solid',
			borderImage: 'linear-gradient(to bottom, transparent, var(--primary-dark), transparent) 1',
			paddingRight: '0.6rem',
		}}
	>
		<FaoLogo />
	</div>
);

const ExactLogo = () => {
	return (
		<div
			className="d-flex f-column justify-content-center align-items-start"
			style={{
				padding: '1.2rem',
				color: 'var(--primary-dark)',
			}}
		>
			<div className="d-flex w-100">
				<div className="montserrat-medium fs-13">
					EX-ACT
				</div>
			</div>
			<span className="montserrat-normal fs-11">
				Environmental eXternalities ACcounting Tool
			</span>
		</div>
	);
};

const FaoLogo = () => {
	const accessToken = useAppSelector(selectCurrentToken);
	const { project } = useAppSelector(selectCurrentProject);
	const { isPublic } = usePermissions();
	const navigate = useAppNavigate();

	const encodedId = useMemo(() =>
		project?.id ? encodeProjectId(project.id) : undefined,
		[project?.id]
	);

	const handleLogoClick = useCallback(() => {
		const hasAccess = accessToken || isPublic;

		if (hasAccess && project) {
			const projectId = isPublic ? encodedId : project.id;
			navigate({ path: `/project/${projectId}/activities` });
		} else if (accessToken) {
			navigate({ path: "/", ignorePublic: true });
		} else {
			navigate({ path: "/auth/login", ignorePublic: true });
		}
	}, [accessToken, isPublic, project, encodedId, navigate]);

	return (
		<img
			src={faoLogo}
			alt="FAO logo"
			className="logo-fao"
			onClick={handleLogoClick}
			style={{ cursor: "pointer" }}
		/>
	);
};

export default Header;

